import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ShieldCheck, Check } from 'lucide-react'
import {
  Card, CardHeader, ErrorState, LoadingState, NoPermissionState, useLocale,
} from '@hk/ui'
import {
  ApiError, fetchPermissionGrid, formatCount, may, nameIn, savePermissionGrid,
  translateError,
  type CapabilityRow, type Grant, type GridRole, type PermissionArea,
  type PermissionGrid,
} from '@hk/core'
import { STRINGS } from '../i18n/strings'
import { Problem } from './StockCounts'

/* ============================================================================
   W-122 - ROLES AND PERMISSIONS: six roles, twenty-six capabilities, one grid.

   THE THIRD STATE IS THE POINT OF THE SCREEN. A grant has had three values since
   Sprint 0 - denied, allowed, and allowed with a reason recorded - and until this
   screen there was no way for anybody outside the source code to see that, let alone
   set it. Pack 5 argues it better than we have anywhere else: "a two-state permission
   model forces a choice between rigidity and no accountability, and the answer people
   actually want is neither."

   AND THE PACK DRAWS ONLY TWO. Its caption says a half-filled box means "with a reason
   recorded"; its mock-up has exactly two cell styles, empty and ticked. The third state
   is described and never rendered, which is why this screen carries a LEGEND rather
   than the pack's one-line footnote - a state nobody has ever seen drawn cannot be
   explained by a sentence at the top of a table.

   THE CELL IS A BUTTON THAT CYCLES, and it cycles through two states or three
   depending on the row. "See tasks assigned to you, with a reason recorded" is
   nonsense - there is no moment at which anybody would type one - so the third state is
   offered only where `mayNeedAReason` says it means something. The SERVER refuses it as
   well, because this screen is not the only way in.

   NOTHING IS SAVED UNTIL SAVE. Twenty-six rows by six columns is a hundred and
   fifty-six cells, and a grid that wrote each tap would make a handover - give
   `admin.roles` to the manager, take it from the administrator - impossible: the second
   tap would be refused as the one that locks everybody out. So changes are collected
   and sent as ONE BATCH, which is also the only form in which the lockout question can
   be asked honestly.
   ========================================================================= */

type Pending = Map<string, Grant>

const key = (roleId: string, code: string) => `${roleId} ${code}`

/** The order a cell cycles through when tapped. Two states, or three where a reason
 *  can actually be typed. */
function next(grant: Grant, mayNeedAReason: boolean): Grant {
  if (grant === 'DENIED') return 'ALLOWED'
  if (grant === 'ALLOWED') return mayNeedAReason ? 'ALLOWED_WITH_REASON' : 'DENIED'
  return 'DENIED'
}

export function RolesPermissions() {
  const { locale } = useLocale()
  const t = STRINGS[locale]
  const [pending, setPending] = useState<Pending>(new Map())
  const [changedOnly, setChangedOnly] = useState(false)
  const [busy, setBusy] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [reload, setReload] = useState(0)

  /* GATED ON THE PERMISSION rather than returned early on it - the same reasoning as
     W-120. Without `enabled` the screen would ask the server for the whole grid before
     deciding it is not allowed to draw it, and a 403 in the log of somebody who never
     should have asked looks exactly like a real one. */
  const mayAdminister = may('admin.roles')

  const grid = useQuery({
    queryKey: ['role-permissions', reload],
    queryFn: ({ signal }) => fetchPermissionGrid({ signal }),
    enabled: mayAdminister,
    retry: false,
  })

  const data: PermissionGrid | undefined = grid.data

  /** What a cell reads NOW: the pending change if there is one, else the saved grant. */
  const currentOf = (row: CapabilityRow, roleId: string): Grant =>
    pending.get(key(roleId, row.code)) ?? row.grants[roleId]?.grant ?? 'DENIED'

  const rows = useMemo(() => {
    const all = data?.capabilities ?? []
    if (!changedOnly) return all
    /* CHANGED MEANS CHANGED FROM WHAT WAS SET UP, and it counts the unsaved edits too.
       A filter that ignored them would hide the row somebody had just touched, which is
       the one row they are looking at. */
    return all.filter((row) => (data?.roles ?? []).some((role) => {
      const cell = row.grants[role.roleId]
      return cell !== undefined && currentOf(row, role.roleId) !== cell.default
    }))
    /* `pending` IS IN THE DEPENDENCIES DELIBERATELY. It is read through `currentOf`
       rather than named in this block, so nothing would complain if it were left out
       — and the filter would then go on showing the rows that were changed before the
       last tap. */
  }, [data, changedOnly, pending])

  /** How many cells differ from the seed, as the screen reads right now. */
  const changed = useMemo(() => {
    let n = 0
    for (const row of data?.capabilities ?? [])
      for (const role of data?.roles ?? []) {
        const cell = row.grants[role.roleId]
        if (cell !== undefined && currentOf(row, role.roleId) !== cell.default) n++
      }
    return n
    // Same reasoning: read through `currentOf`, so it has to be named here.
  }, [data, pending])

  if (!mayAdminister) return <NoPermissionState />

  if (grid.isLoading && !grid.error) return <LoadingState rows={8} />

  if (grid.error && !data) {
    if (grid.error instanceof ApiError && grid.error.code === 'permission.denied')
      return <NoPermissionState />
    const translated = translateError(grid.error, locale)
    return (
      <ErrorState
        body={translated.message}
        onRetry={grid.refetch}
        {...(translated.correlationId ? { correlationId: translated.correlationId } : {})}
      />
    )
  }

  if (data === undefined) return <LoadingState rows={8} />

  const board = data

  function tap(row: CapabilityRow, role: GridRole) {
    if (!board.mayEdit) return
    const savedGrant = row.grants[role.roleId]?.grant ?? 'DENIED'
    const asked = next(currentOf(row, role.roleId), row.mayNeedAReason)
    setSaved(false)
    setPending((was) => {
      const now = new Map(was)
      /* BACK TO WHERE IT STARTED IS NOT A CHANGE. Cycling a cell all the way round
         must leave nothing to save, or Save would send a change that changes nothing
         and the lockout guard would be asked a question nobody posed. */
      if (asked === savedGrant) now.delete(key(role.roleId, row.code))
      else now.set(key(role.roleId, row.code), asked)
      return now
    })
  }

  async function save() {
    setBusy(true); setError(null)
    try {
      const changes = [...pending.entries()].map(([composite, grant]) => {
        const [roleId, permissionCode] = composite.split(' ')
        return { roleId: roleId!, permissionCode: permissionCode!, grant }
      })
      await savePermissionGrid(changes)
      setPending(new Map())
      setSaved(true)
      setReload((n) => n + 1)
    } catch (caught: unknown) {
      /* THE GRID IS LEFT EXACTLY AS IT WAS. A refused save is usually the lockout
         guard, and the answer is to give the permission to somebody else IN THE SAME
         SAVE - which is impossible if the refusal has thrown away what was typed. */
      setError(caught)
    } finally {
      setBusy(false)
    }
  }

  const roles = board.roles

  return (
    <div className="flex flex-col gap-3.5">
      {error !== null && <Problem error={error} locale={locale} />}

      <Card>
        <CardHeader
          title={t.rolTitle}
          icon={<ShieldCheck className="size-4" />}
          actions={
            <div className="flex items-center gap-2">
              {pending.size > 0 && (
                <button
                  type="button"
                  onClick={() => { setPending(new Map()); setError(null) }}
                  className="rounded-hk border border-border px-2.5 py-1.5 text-xs
                             font-medium hover:bg-muted cursor-pointer"
                >
                  {t.rolDiscard}
                </button>
              )}
              <button
                type="button"
                onClick={() => { void save() }}
                disabled={pending.size === 0 || busy}
                className="inline-flex items-center gap-1.5 rounded-hk bg-[var(--hk-primary)]
                           px-3 py-1.5 text-xs font-medium text-white cursor-pointer
                           disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Check className="size-3.5" />
                {busy ? t.rolSaving : t.rolSave}
              </button>
            </div>
          }
        />

        <div className="flex flex-col gap-3 p-3.5">
          <p className="max-w-[68ch] text-[12.5px] leading-relaxed text-muted-foreground">
            {t.rolWhy}
          </p>

          {/* THE LEGEND, because the third state has never been drawn anywhere. */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 rounded-hk
                          border border-border bg-muted/40 px-3 py-2 text-[12px]">
            <Legend grant="DENIED" label={t.rolDenied} />
            <Legend grant="ALLOWED" label={t.rolAllowed} />
            <Legend grant="ALLOWED_WITH_REASON" label={t.rolAllowedWithReason} />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Chip
              label={t.rolAll} active={!changedOnly} onClick={() => setChangedOnly(false)}
            />
            {/* THE COUNT IS ON THE FILTER, because "changed from default" with no number
                beside it is a filter nobody presses - there is no way to tell whether it
                leads to twelve rows or none. */}
            <Chip
              label={`${t.rolChangedOnly} (${formatCount(changed, locale)})`}
              active={changedOnly} onClick={() => setChangedOnly(true)}
            />
            <span className="grow" />
            {pending.size > 0 && (
              <span className="text-[12px] font-medium text-[#b45309] dark:text-[#fbbf24]">
                {t.rolUnsaved.replace('{count}', formatCount(pending.size, locale))}
              </span>
            )}
            {pending.size === 0 && saved && (
              <span className="text-[12px] text-muted-foreground">{t.rolSaved}</span>
            )}
          </div>
        </div>

        {rows.length === 0 ? (
          <p className="border-t border-border px-3.5 py-6 text-center text-[13px]
                        text-muted-foreground">
            {t.rolNothingChanged}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-[13px]">
              <thead>
                <tr className="border-b border-border text-[11.5px] text-muted-foreground">
                  <th className="px-3 py-2 text-start font-medium">{t.rolColArea}</th>
                  <th className="px-3 py-2 text-start font-medium">{t.rolColCapability}</th>
                  {roles.map((role) => (
                    <th key={role.roleId} className="px-2 py-2 text-center font-medium">
                      <bdi>{nameIn(locale, role.name, role.nameAr)}</bdi>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.code} className="border-b border-border/60 last:border-0">
                    <td className="px-3 py-2 align-top text-[11.5px] text-muted-foreground">
                      {areaName(row.area, locale)}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <span className="block">{capabilityName(row.code, locale)}</span>
                      {/* THE CODE, UNDER THE SENTENCE AND NEVER TRANSLATED. It is what
                          the server checks and what appears in a log, and an
                          administrator on the telephone to us needs to be able to read
                          it out. */}
                      <span className="block text-[10.5px] text-muted-foreground">
                        {row.code}
                      </span>
                      {/* GRANTED, AND ENFORCED BY NOTHING YET. Somebody setting one of
                          these is entitled to know it changes nothing today rather than
                          discovering it in UAT. */}
                      {row.enforcedBy.startsWith('nothing yet') && (
                        <span className="mt-0.5 block text-[10.5px] text-[#b45309]
                                         dark:text-[#fbbf24]">
                          {t.rolNotEnforcedYet}
                        </span>
                      )}
                    </td>
                    {roles.map((role) => {
                      const cell = row.grants[role.roleId]
                      const grant = currentOf(row, role.roleId)
                      const isPending = pending.has(key(role.roleId, row.code))
                      const differs = cell !== undefined && grant !== cell.default
                      return (
                        <td key={role.roleId} className="px-2 py-2 text-center align-top">
                          <Cell
                            grant={grant}
                            differs={differs}
                            isPending={isPending}
                            disabled={!board.mayEdit}
                            label={cellLabel(locale, role, row, grant)}
                            onClick={() => tap(row, role)}
                          />
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}

/** One cell. A button, because it does something - and a button that says in words what
 *  it is and what it means, because a coloured square says neither. */
function Cell({
  grant, differs, isPending, disabled, label, onClick,
}: {
  grant: Grant; differs: boolean; isPending: boolean; disabled: boolean
  label: string; onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={`relative inline-flex size-6 items-center justify-center rounded-hk
                  ${disabled ? 'cursor-default' : 'cursor-pointer hover:opacity-80'}
                  ${isPending ? 'ring-2 ring-[#f59e0b]' : ''}`}
    >
      <Box grant={grant} />
      {/* CHANGED FROM WHAT WAS SET UP, marked on the cell itself. The filter above
          finds the rows; this says which cell in a row of six it was. */}
      {differs && !isPending && (
        <span
          aria-hidden="true"
          className="absolute -end-0.5 -top-0.5 size-1.5 rounded-full bg-[var(--hk-primary)]"
        />
      )}
    </button>
  )
}

/** The box itself, and the ONLY drawing of the third state anywhere in this product.
 *
 *  THE HALF IS BOTTOM-TO-TOP, NOT LEFT-TO-RIGHT. A box filled on its left half means
 *  something different in a screen that reads right to left - or rather, it means
 *  nothing at all, because it would flip with the page and the reader would have to
 *  know which way round the language was to read the setting. A horizontal split is the
 *  same box in both. */
function Box({ grant }: { grant: Grant }) {
  if (grant === 'ALLOWED')
    return (
      <span
        aria-hidden="true"
        className="flex size-4 items-center justify-center rounded-[4px]
                   bg-[var(--hk-primary)] text-white"
      >
        <Check className="size-3" strokeWidth={3} />
      </span>
    )

  if (grant === 'ALLOWED_WITH_REASON')
    return (
      <span
        aria-hidden="true"
        className="relative block size-4 overflow-hidden rounded-[4px]
                   border border-[var(--hk-primary)] bg-background"
      >
        <span className="absolute inset-x-0 bottom-0 h-1/2 bg-[var(--hk-primary)]" />
      </span>
    )

  return (
    <span
      aria-hidden="true"
      className="block size-4 rounded-[4px] border border-border bg-background"
    />
  )
}

function Legend({ grant, label }: { grant: Grant; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-muted-foreground">
      <Box grant={grant} />
      {label}
    </span>
  )
}

function Chip({
  label, active, onClick,
}: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-hk border px-2.5 py-1 text-xs font-medium cursor-pointer
                  ${active
                    ? 'border-[var(--hk-primary)] bg-[var(--hk-primary)] text-white'
                    : 'border-border hover:bg-muted'}`}
    >
      {label}
    </button>
  )
}

/** WHAT A SCREEN READER AND A TOOLTIP SAY: who, what, and which of the three - in
 *  words. The square is the shorthand; this is the fact. */
function cellLabel(
  locale: 'en' | 'ar', role: GridRole, row: CapabilityRow, grant: Grant,
): string {
  const t = STRINGS[locale]
  const state = grant === 'ALLOWED'
    ? t.rolAllowed
    : grant === 'ALLOWED_WITH_REASON' ? t.rolAllowedWithReason : t.rolDenied
  const who = nameIn(locale, role.name, role.nameAr)
  return `${who} - ${capabilityName(row.code, locale)} - ${state}`
}

/* THE AREA AND CAPABILITY NAMES ARE LOOKED UP BY CODE, and a code with no sentence
   falls back to the code itself rather than to an empty cell. The catalogue is on the
   server and the sentences are here; the day somebody adds a permission and forgets the
   string, the grid shows `stock.writeOff` and remains usable. A test fails on the
   missing sentence long before anybody sees that. */
/** Only the keys whose value is a plain sentence. The dictionary also holds a few
 *  small maps (room tones, filter labels), and a lookup that could name one of those
 *  would compile and then render `[object Object]` in a table cell. */
type Sentence = {
  [K in keyof typeof STRINGS['en']]: typeof STRINGS['en'][K] extends string ? K : never
}[keyof typeof STRINGS['en']]

const AREAS: Record<PermissionArea, Sentence> = {
  ROOMS: 'rolAreaRooms',
  WORK: 'rolAreaWork',
  REQUESTS: 'rolAreaRequests',
  FAULTS: 'rolAreaFaults',
  PUBLIC_AREAS: 'rolAreaAreas',
  INVENTORY: 'rolAreaInventory',
  REPORTS: 'rolAreaReports',
  FILES: 'rolAreaFiles',
  ADMINISTRATION: 'rolAreaAdministration',
}

const CAPABILITIES: Record<string, Sentence> = {
  'room.viewReadiness': 'rolCapRoomViewReadiness',
  'room.setOutOfService': 'rolCapRoomSetOutOfService',
  'room.overrideToClean': 'rolCapRoomOverrideToClean',
  'room.syncFromPms': 'rolCapRoomSyncFromPms',
  'task.start': 'rolCapTaskStart',
  'task.submit': 'rolCapTaskSubmit',
  'task.assign': 'rolCapTaskAssign',
  'task.raise': 'rolCapTaskRaise',
  'task.inspect': 'rolCapTaskInspect',
  'task.cancel': 'rolCapTaskCancel',
  'roster.edit': 'rolCapRosterEdit',
  'request.raise': 'rolCapRequestRaise',
  'request.fulfil': 'rolCapRequestFulfil',
  'issue.raise': 'rolCapIssueRaise',
  'fault.downgradeBlocking': 'rolCapFaultDowngradeBlocking',
  'area.clean': 'rolCapAreaClean',
  'area.setOutOfService': 'rolCapAreaSetOutOfService',
  'stock.move': 'rolCapStockMove',
  'stock.adjust': 'rolCapStockAdjust',
  /* W-104 (Sprint 67). Its own cell because moving four towels upstairs and deciding what
     the fourth floor should always hold are different acts by different people. */
  'admin.stockPars': 'rolCapAdminStockPars',
  /* CQ-138. Its own cell precisely so a hotel can move it without handing the roster
     over with it — see `LinenService.AdjustPermission`. */
  'linen.adjust': 'rolCapLinenAdjust',
  'report.attendantProductivity': 'rolCapReportAttendantProductivity',
  /* ONE LINE PER REPORT, because they are one permission per report — a hotel that wants
     its store keeper to see the linen figures without also seeing every attendant's output
     has to be able to say so on this screen. */
  'report.roomsPerDay': 'rolCapReportRoomsPerDay',
  'report.linenLoss': 'rolCapReportLinenLoss',
  'report.requestLateness': 'rolCapReportRequestLateness',
  'report.inspectionFailures': 'rolCapReportInspectionFailures',
  /* The three the client confirmed on 18 September (Sprint 66). The other three of the
     six were already built under other names — inspection failures, request lateness and
     linen loss — which the screen inventory had been calling unbuilt. */
  'report.faultsDowntime': 'rolCapReportFaultsDowntime',
  'report.loadThroughDay': 'rolCapReportLoadThroughDay',
  'report.consumables': 'rolCapReportConsumables',
  'file.remove': 'rolCapFileRemove',
  'admin.rooms': 'rolCapAdminRooms',
  'admin.linenStandards': 'rolCapAdminLinenStandards',
  /* W-135. A READ over five other screens' data plus one number a person types about
     their own hotel — see `Permissions.All` for why it is not folded into any of them. */
  'admin.goLive': 'rolCapAdminGoLive',
  /* W-129, CQ-130. Separate from `admin.roles` so a hotel can hand the Arabic proofread
     to somebody without also handing them the permission grid. */
  'admin.configuration': 'rolCapAdminConfiguration',
  'admin.requestCategories': 'rolCapAdminRequestCategories',
  'admin.faultCategories': 'rolCapAdminFaultCategories',
  'admin.qrCodes': 'rolCapAdminQrCodes',
  'admin.checklists': 'rolCapAdminChecklists',
  'admin.users': 'rolCapAdminUsers',
  'admin.roles': 'rolCapAdminRoles',
  'admin.translations': 'rolCapAdminTranslations',
  'admin.activity': 'rolCapAdminActivity',
}

export function areaName(area: PermissionArea, locale: 'en' | 'ar'): string {
  const k = AREAS[area]
  return k === undefined ? area : STRINGS[locale][k]
}

export function capabilityName(code: string, locale: 'en' | 'ar'): string {
  const k = CAPABILITIES[code]
  return k === undefined ? code : STRINGS[locale][k]
}
